import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getAllServices, createService, updateService } from '../../lib/api'
import { serviceSchema } from '../../lib/schemas'
import { slugify } from '../../lib/utils'
import { Input } from '../../components/ui/Input'
import { IconInput } from '../../components/ui/IconInput'
import { Toggle } from '../../components/ui/Toggle'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { RichTextEditor } from '../../components/ui/RichTextEditor'

export default function ServiceForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: services } = useQuery({ queryKey: ['services'], queryFn: getAllServices, enabled: isEdit })

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(serviceSchema),
    defaultValues: { title: '', slug: '', icon: '', gradient: '', description: '', price: '', order: 0, isActive: true },
  })

  useEffect(() => {
    if (isEdit && services) {
      const item = services.find(s => s.id === id)
      if (item) reset(item)
    }
  }, [services, id, isEdit, reset])

  const titleValue = watch('title')
  useEffect(() => {
    if (!isEdit && titleValue) setValue('slug', slugify(titleValue))
  }, [titleValue, isEdit, setValue])

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? updateService(id, data) : createService(data),
    onSuccess: (result) => {
      qc.setQueryData(['services'], old =>
        isEdit
          ? (old ?? []).map(s => s.id === id ? result : s)
          : [...(old ?? []), result]
      )
      toast.success(isEdit ? 'Service updated' : 'Service created')
      navigate('/services')
    },
    onError: (e) => toast.error(e.message),
  })

  if (isEdit && !services) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  return (
    <div className="">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/services"><Button variant="ghost" size="sm"><i className="ti ti-arrow-left" /></Button></Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Service' : 'New Service'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Service offering details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-6">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Details</h2>
          <Input label="Title" placeholder="Business Website" error={errors.title?.message} {...register('title')} />
          <Input label="Slug" placeholder="business-website" hint="Auto-generated from title" error={errors.slug?.message} {...register('slug')} />
          <div className="grid grid-cols-2 gap-4">
            <IconInput label="Icon class" error={errors.icon?.message} value={watch('icon')} {...register('icon')} />
            <Input label="Price" placeholder="From $299" error={errors.price?.message} {...register('price')} />
          </div>
          <Input label="Gradient" placeholder="from-blue-500 to-indigo-600" hint="Tailwind gradient classes" error={errors.gradient?.message} {...register('gradient')} />
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Description</h2>
          <Controller name="description" control={control} render={({ field }) => (
            <RichTextEditor placeholder="Describe what this service includes..." value={field.value} onChange={field.onChange} error={errors.description?.message} />
          )} />
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Settings</h2>
          <Input label="Sort order" type="number" placeholder="0" error={errors.order?.message} {...register('order')} />
          <Controller name="isActive" control={control} render={({ field }) => (
            <Toggle label="Active" description="Show on the public site" checked={field.value} onChange={field.onChange} />
          )} />
        </section>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            {isEdit ? 'Update Service' : 'Create Service'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/services')}>Cancel</Button>
        </div>
      </form>
    </div>
  )
}
